/**
 * Reusable validators
 */

import { z } from 'zod';

/**
 * Email validator
 */
export const emailSchema = z.email('validation.email.invalid');

/**
 * UUID validator
 */
export const uuidSchema = z.uuid('validation.uuid.invalid');

/**
 * Phone number validator (basic)
 */
export const phoneSchema = z
  .string()
  .regex(/^\+?[1-9]\d{1,14}$/, 'validation.phone.invalid')
  .optional()
  .nullable();

/**
 * URL validator
 */
export const urlSchema = z.url('validation.url.invalid').optional().nullable();

/**
 * Country code validator (ISO 3166-1 alpha-2).
 * Used for originCountry, destinationCountry, targetCountry, currentCountry, nationality.
 * Canonical source for display data: @tonnie/foundation (COUNTRIES, getCountryName, getCountryFlag).
 */
export const countryCodeSchema = z
  .string()
  .length(2, 'validation.countryCode.length')
  .toUpperCase();

/**
 * The destinations open TODAY — `OPEN_DESTINATIONS` in `@tonnie/foundation`,
 * restated because this package depends on zod alone (the household
 * vocabulary's rule). `open-destinations.spec.ts` in platform-api asserts the
 * two lists are identical, so they cannot drift.
 */
export const OPEN_DESTINATION_CODES = ['PT'] as const;

/**
 * A journey's or an invitation's destination: an ISO alpha-2 code that is
 * OPEN. A country with no relocation kit, CheckUp persona or QultureUp world is
 * refused at the door, not only hidden in a picker.
 */
export const openDestinationCountrySchema = countryCodeSchema.refine(
  (code) => (OPEN_DESTINATION_CODES as readonly string[]).includes(code),
  'validation.destination.notOpen'
);

/**
 * Locale validator
 */
export const localeSchema = z.enum(['en', 'pt', 'es', 'de', 'it', 'fr']);

/**
 * Pagination params
 */
export const paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
});

/**
 * Date range validator
 */
export const dateRangeSchema = z
  .object({
    from: z.coerce.date(),
    to: z.coerce.date(),
  })
  .refine((data) => data.from <= data.to, {
    message: 'validation.dateRange.endAfterStart',
    path: ['to'],
  });

/**
 * Non-empty string
 */
export const nonEmptyString = z.string().min(1, 'validation.field.required');

/**
 * Score validator (0-100)
 */
export const scoreSchema = z.number().min(0).max(100);

/**
 * Pillar score validator (0-5)
 */
export const pillarScoreSchema = z.number().int().min(0).max(5);
